import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useReservation } from '../context/ReservationContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const reservation = useReservation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/fotos', label: t.nav.photos },
    { path: '/menu', label: t.nav.menu },
    { path: '/events', label: t.nav.events },
    { path: '/private', label: t.nav.private },
    { path: '/contact', label: t.nav.contact },
    { path: '/locatie', label: t.nav.location },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-nav py-3' : 'bg-transparent py-5'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-logo text-xl md:text-2xl font-bold tracking-wider text-[#C8A46A]"
            style={{ fontFamily: "'Cinzel', serif" }}
            data-testid="navbar-logo"
          >
            BARBAAR
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-body text-sm tracking-widest uppercase transition-colors ${
                  location.pathname === link.path
                    ? 'text-copper'
                    : 'text-[#A1A1A1] hover:text-[#EDEDED]'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 text-[#A1A1A1] hover:text-[#EDEDED] transition-colors font-body text-sm"
              data-testid="language-toggle"
            >
              <Globe size={16} />
              <span className="uppercase">{language === 'nl' ? 'EN' : 'NL'}</span>
            </button>

            <button
              onClick={() => reservation.open()}
              className="bg-[#C8A46A] hover:bg-[#B89355] text-[#0A0A0A] font-body text-sm tracking-wider px-6 py-2 rounded-sm transition-colors"
              data-testid="nav-reserve-btn"
            >
              {t.nav.reserve}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 text-[#A1A1A1] hover:text-[#EDEDED] transition-colors font-body text-sm"
              data-testid="mobile-language-toggle"
            >
              <Globe size={16} />
              <span className="uppercase">{language === 'nl' ? 'EN' : 'NL'}</span>
            </button>
            <button
              className="text-[#EDEDED] p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-nav border-t border-[#2A2A2A]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block font-body text-base tracking-wide transition-colors ${
                    location.pathname === link.path
                      ? 'text-copper'
                      : 'text-[#A1A1A1]'
                  }`}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => { reservation.open(); setIsOpen(false); }}
                className="block w-full bg-[#C8A46A] hover:bg-[#B89355] text-[#0A0A0A] font-body text-base tracking-wide px-4 py-3 rounded-sm text-center mt-4"
                data-testid="mobile-reserve-btn"
              >
                {t.nav.reserve}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
